import React, { useState } from 'react';
import { App, Button } from 'antd';

import { downloadItemAttachment } from '../../services/attachments';
import { filesOf, showsAsPicture } from '../../utils/fileValues';
import { normalizeId } from '../../utils/ids';
import { DOWNLOAD } from '../icons';
import AttachmentPreviewModal, { canOpenAttachment } from './AttachmentPreviewModal';
import { FileChip, FilePicture } from './FileChip';
import FileScanNote, { confirmFlaggedFile } from './FileScanNote';
import { scanNoteFor } from '../../utils/fileScan';
import { PublicFilePicture, PublicFileValue, usePublicFiles } from './PublicFiles';
import { anchorOf } from '../../platform';

/**
 * A file element's value in the read view (docs/plans/data-types-file-element.md
 * §6, with 10a's sizes):
 *
 *   - a photo shows its `md` (800 px) at content width; a click or a tap
 *     opens 10a's viewer — the 2,048 px `lg`, then View full resolution — which
 *     takes the whole screen on a phone;
 *   - a document (a PDF, or a photo without its `md`) shows the file chip with
 *     its size, and Download as the value action; its name opens the viewer
 *     where the viewer can show it.
 *
 * A list shows each file in order. An id with no `files` facts entry shows
 * nothing. Download and the photo's open target keep the 44 px floor on a
 * touch screen (data-types.css) — not the shared value-action trigger's 20 px.
 *
 * `canDownload` false (a reader who may not take copies) still sees every
 * thumbnail and may open a photo's `lg` (F8: looking), with no Download.
 *
 * The malware verdict (PR 10e) shows under each file: nothing when clean,
 * "Not checked yet" quietly, and for a flagged file the warning line — and
 * Download asks first (components/common/FileScanNote.js).
 *
 * On a PUBLIC page (PR 10c) the shared page's PublicFilesProvider answers,
 * and the value draws from the public facts instead (components/common/
 * PublicFiles.js): `md` and `lg` from the public routes, Download for a file
 * a visitor may take and "Sign in to download" for the rest.
 */
export default function FileValue({ item, value, canDownload = true }) {
	const { message, modal } = App.useApp();
	const publicFiles = usePublicFiles();
	const itemId = normalizeId(item?._id);
	const files = filesOf(item, value);
	const [viewing, setViewing] = useState(null);

	if (!files.length) return null;
	if (publicFiles) return <PublicFileValue code={publicFiles.code} item={item} value={value} />;

	const download = async (file, event) => {
		const anchor = anchorOf(event);
		if (!(await confirmFlaggedFile(modal, file))) return;
		try {
			await downloadItemAttachment(itemId, file._id, file.originalName, { anchor });
		} catch (error) {
			const said = error?.response?.data?.message;
			message.error(typeof said === 'string' && said ? said : 'Unable to download that file.');
		}
	};

	return (
		<div className="file-value" data-testid="file-value">
			{files.map((file) => {
				const opens = canOpenAttachment(file, { canDownload });
				if (showsAsPicture(file, 'md')) {
					const picture = <FilePicture itemId={itemId} file={file} size="md" chipSize="md" />;
					return (
						<React.Fragment key={file._id}>
							{opens ? (
								<button
									type="button"
									className="file-value__photo"
									aria-label={`Open ${file.originalName || 'photo'}`}
									onClick={() => setViewing(file)}
								>
									{picture}
								</button>
							) : <div className="file-value__photo">{picture}</div>}
							<FileScanNote file={file} />
						</React.Fragment>
					);
				}
				const chip = <FileChip file={file} showSize />;
				return (
					<React.Fragment key={file._id}>
					<div className="file-value__doc">
						{opens ? (
							<button
								type="button"
								className="file-value__open"
								aria-label={`Open ${file.originalName || 'file'}`}
								onClick={() => setViewing(file)}
							>
								{chip}
							</button>
						) : chip}
						{canDownload ? (
							<Button
								className="file-value__download"
								size="small"
								icon={<DOWNLOAD />}
								aria-label={`Download ${file.originalName || 'file'}`}
								onClick={event => download(file, event)}
							>
								Download
							</Button>
						) : null}
					</div>
					<FileScanNote file={file} />
					</React.Fragment>
				);
			})}
			<AttachmentPreviewModal
				attachment={viewing}
				itemId={itemId}
				open={Boolean(viewing)}
				onClose={() => setViewing(null)}
				canDownload={canDownload}
			/>
		</div>
	);
}

/**
 * A file element on a TILE (plan §6, F9): a span-1 body field. A photo is a
 * 72 px cover-cropped `sm` (the caller hides the label over it); anything
 * else is the dense chip. A list shows its first file — a tile says what a
 * record is, the read view lists every one. Not a door of its own: the tile
 * is what opens the item. Where the chip shows, the malware verdict rides
 * beside it in one line ("Flagged", "Not checked yet"; PR 10e) and gives way
 * before the chip's name; a picture says nothing (its pixels are re-encoded).
 */
export function FileTileValue({ item, value, dense = false }) {
	const publicFiles = usePublicFiles();
	const [first] = filesOf(item, value);
	if (!first) return null;
	// A public grid draws the payload's inline `sm`: no byte request per tile.
	if (publicFiles) {
		return !dense && showsAsPicture(first, 'sm')
			? <PublicFilePicture code={publicFiles.code} file={first} size="sm" className="file-picture--tile" />
			: <FileChip file={first} size="sm" />;
	}
	// A dense (multi-row) arranged body reads each field as one line: no room
	// for 72 px, so the chip.
	if (!dense && showsAsPicture(first, 'sm')) {
		return <FilePicture itemId={normalizeId(item?._id)} file={first} size="sm" className="file-picture--tile" />;
	}
	if (!scanNoteFor(first)) return <FileChip file={first} size="sm" />;
	return (
		<span className="file-tile">
			<FileChip file={first} size="sm" />
			<FileScanNote file={first} compact />
		</span>
	);
}

/** Does this value read as a picture on a tile (so its label is hidden)? */
export const tileShowsPicture = (item, value) => {
	const [first] = filesOf(item, value);
	return Boolean(first) && showsAsPicture(first, 'sm');
};

/**
 * A file element in a TABLE cell (plan §6): a 32 px `sm`, or the chip at
 * `xs`; a list shows its first file and "+N" for the rest.
 */
export function FileCellValue({ item, value }) {
	const publicFiles = usePublicFiles();
	const files = filesOf(item, value);
	if (!files.length) return null;
	const [first] = files;
	const more = files.length - 1;
	const picture = publicFiles
		? <PublicFilePicture code={publicFiles.code} file={first} size="sm" chipSize="xs" className="file-picture--cell" />
		: <FilePicture itemId={normalizeId(item?._id)} file={first} size="sm" chipSize="xs" className="file-picture--cell" />;
	return (
		<span className="file-cell" data-testid="file-cell">
			{showsAsPicture(first, 'sm') ? picture : <FileChip file={first} size="xs" />}
			{more > 0 ? <span className="file-cell__more">+{more}</span> : null}
		</span>
	);
}
